import { Activity, CreditCard, Plug, Webhook } from "lucide-react";
import { Navigate } from "react-router";
import { defineFeature } from "../types";

/**
 * Integrations — payment gateways, WhatsApp, Slack, Zapier-style webhooks and
 * Google Drive (roadmap item 16, PRD US-16.1 … US-16.7).
 *
 * The hub lives at /integrations. ENTITY_ROUTES sends `integration` records to
 * /settings/integrations and `webhook` records to /settings/webhooks (search
 * results, "integration failed" notifications, the Slack alert link), so those
 * two paths redirect into the hub. The Slack and Google OAuth redirect URIs
 * the API registers are /settings/integrations/<provider>/callback. All of
 * these are more specific than the settings shell's `settings/*`, so they win.
 *
 * /pay/thanks and /pay/cancelled are where Stripe Checkout returns a payer
 * when the agency configured no page of its own — public, no session.
 */
export default defineFeature({
  key: "integrations",
  title: "Integrations",
  entities: ["integration", "webhook"],
  routes: [
    { path: "integrations", lazy: () => import("./page") },
    { path: "integrations/health", lazy: () => import("./health") },
    { path: "integrations/webhooks", lazy: () => import("./webhooks") },
    { path: "integrations/:provider", lazy: () => import("./provider") },
    { path: "settings/integrations/:provider/callback", lazy: () => import("./oauth-callback") },
    { path: "settings/integrations", element: <Navigate to="/integrations" replace /> },
    { path: "settings/webhooks", element: <Navigate to="/integrations/webhooks" replace /> },
  ],
  publicRoutes: [
    { path: "/pay/thanks", lazy: () => import("./pay-return") },
    { path: "/pay/cancelled", lazy: () => import("./pay-return") },
  ],
  nav: [
    {
      label: "Integrations",
      to: "/integrations",
      icon: Plug,
      group: "workspace",
      order: 20,
      permission: "integrations:manage",
      description: "Payments, WhatsApp, Slack, Drive and Zapier",
    },
  ],
  commands: [
    { key: "integrations.health", label: "Integration health", icon: Activity, to: "/integrations/health", group: "navigate", permission: "integrations:manage", keywords: ["failing", "webhook", "error", "log"] },
    { key: "integrations.webhooks", label: "Webhooks & Zapier", icon: Webhook, to: "/integrations/webhooks", group: "navigate", permission: "integrations:manage", keywords: ["zapier", "make", "n8n", "api"] },
    { key: "integrations.razorpay", label: "Connect Razorpay", icon: CreditCard, to: "/integrations/razorpay", group: "actions", permission: "integrations:manage", keywords: ["upi", "payment gateway", "pay now"] },
  ],
});
